/**
 * The number-format model and the display engine over it.
 *
 * A `NumberFormat` is a small, fully-populated record rather than an Excel format
 * string. Requirement 6.1 asks for seven categories, not for the whole
 * `#,##0.00_);[Red]\(#,##0.00\)` language, and a closed record makes interning,
 * serialization, and the round trip in Property 20 straightforward. The XLSX
 * codec (task 21) maps this record onto format strings at the boundary.
 *
 * Two rules from the requirements shape everything here:
 *
 * - **Decimal places are a display concern only** (Requirement 6.2). `formatValue`
 *   never writes back, and the stored `CellValue` keeps full `Float64` precision.
 * - **`#####` is for numerics only** (Requirement 6.7). Text overflows its cell
 *   instead, which is what Excel does, so the marker is substituted only when the
 *   value is a number.
 *
 * Width measurement is deliberately a pure function of the text and the font
 * size rather than a canvas `measureText` call: the kernel worker has no canvas,
 * the overflow decision has to be reproducible in tests, and Property 23 states
 * the marker rule in terms of "the measured width".
 */

import type { CellValue } from '../types.js';
import {
  isDisplayableSerial,
  partsFromSerial,
  type DateParts,
} from './serial.js';

/** The seven number-format categories of Requirement 6.1. */
export type NumberFormatCategory =
  | 'general'
  | 'number'
  | 'currency'
  | 'percentage'
  | 'date'
  | 'time'
  | 'text';

/** Date layouts the display engine and the input classifier agree on. */
export const DATE_PATTERNS = [
  'yyyy-mm-dd',
  'mm/dd/yyyy',
  'm/d/yyyy',
  'yyyy-mm-dd hh:mm',
  'yyyy-mm-dd hh:mm:ss',
] as const;

/** Time-of-day layouts. */
export const TIME_PATTERNS = ['hh:mm', 'hh:mm:ss'] as const;

export type DatePattern = (typeof DATE_PATTERNS)[number];
export type TimePattern = (typeof TIME_PATTERNS)[number];
export type DateTimePattern = DatePattern | TimePattern;

/** Highest decimal-place count accepted; matches `Number.prototype.toFixed`'s useful range. */
export const MAX_DECIMALS = 15;

/** Default font size in CSS pixels, used for width measurement. */
export const DEFAULT_FONT_SIZE_PX = 12;

/** Average glyph advance as a fraction of the font size, for the width estimate. */
export const CHAR_WIDTH_RATIO = 0.62;

/** Horizontal padding a cell reserves inside its column width. */
export const CELL_PADDING_PX = 6;

/** What the Grid_Renderer paints when a numeric value does not fit (Requirement 6.7). */
export const OVERFLOW_MARKER = '#####';

/**
 * A number format.
 *
 * Every field is always present, so two formats are equal exactly when their
 * fields are equal — which is what makes interning a string-keyed map lookup.
 * Fields that a category ignores (`decimals` for `date`, `pattern` for `number`)
 * are still carried, so switching category in the format dialog does not lose the
 * user's other choices.
 */
export interface NumberFormat {
  category: NumberFormatCategory;
  /** Displayed decimal places for `number`, `currency`, and `percentage`. */
  decimals: number;
  /** Thousands grouping for `number` and `currency`. */
  grouping: boolean;
  /** Currency symbol for the `currency` category. */
  currency: string;
  /** Layout for the `date` and `time` categories. */
  pattern: DateTimePattern;
}

/** The `general` format: numbers print as their shortest exact decimal text. */
export const GENERAL_FORMAT: NumberFormat = Object.freeze({
  category: 'general' as NumberFormatCategory,
  decimals: 0,
  grouping: false,
  currency: '$',
  pattern: 'yyyy-mm-dd' as DateTimePattern,
});

function clampDecimals(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(Math.max(Math.trunc(n), 0), MAX_DECIMALS);
}

/** Builds a normalized `NumberFormat`, filling every unset field from `general`. */
export function numberFormat(patch: Partial<NumberFormat> = {}): NumberFormat {
  return {
    category: patch.category ?? GENERAL_FORMAT.category,
    decimals: clampDecimals(patch.decimals ?? GENERAL_FORMAT.decimals),
    grouping: patch.grouping ?? GENERAL_FORMAT.grouping,
    currency: patch.currency ?? GENERAL_FORMAT.currency,
    pattern: patch.pattern ?? GENERAL_FORMAT.pattern,
  };
}

/** Canonical key for interning. Field order is fixed, so the key is stable. */
export function numberFormatKey(fmt: NumberFormat): string {
  return [
    fmt.category,
    fmt.decimals,
    fmt.grouping ? 'g' : '-',
    fmt.currency,
    fmt.pattern,
  ].join('\u0001');
}

export function sameNumberFormat(a: NumberFormat, b: NumberFormat): boolean {
  return numberFormatKey(a) === numberFormatKey(b);
}

/* ----------------------------------------------------------------- numerics */

/** `-0` prints as `0`; everything else uses the shortest round-tripping form. */
export function generalNumberText(v: number): string {
  if (Object.is(v, -0)) return '0';
  return String(v);
}

function groupThousands(intDigits: string): string {
  let out = '';
  for (let i = 0; i < intDigits.length; i++) {
    if (i > 0 && (intDigits.length - i) % 3 === 0) out += ',';
    out += intDigits.charAt(i);
  }
  return out;
}

/**
 * `|v|` with exactly `decimals` decimal places, optionally grouped. The sign is
 * handled by the caller so `-0.001` at two places prints as `0.00` rather than
 * `-0.00`.
 */
function fixedMagnitude(v: number, decimals: number, grouping: boolean): string {
  const text = Math.abs(v).toFixed(clampDecimals(decimals));
  const dot = text.indexOf('.');
  const intPart = dot === -1 ? text : text.slice(0, dot);
  const fracPart = dot === -1 ? '' : text.slice(dot);
  return (grouping ? groupThousands(intPart) : intPart) + fracPart;
}

/** True when the rendered magnitude is zero, so the sign can be dropped. */
function magnitudeIsZero(magnitude: string): boolean {
  return !/[1-9]/.test(magnitude);
}

function signed(v: number, magnitude: string): string {
  if (v >= 0 || magnitudeIsZero(magnitude)) return magnitude;
  return `-${magnitude}`;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function pad4(n: number): string {
  return String(n).padStart(4, '0');
}

/** Renders calendar parts through one of the supported layouts. */
export function formatDateParts(parts: DateParts, pattern: DateTimePattern): string {
  const date = {
    'yyyy-mm-dd': `${pad4(parts.year)}-${pad2(parts.month)}-${pad2(parts.day)}`,
    'mm/dd/yyyy': `${pad2(parts.month)}/${pad2(parts.day)}/${pad4(parts.year)}`,
    'm/d/yyyy': `${parts.month}/${parts.day}/${pad4(parts.year)}`,
  };
  const hm = `${pad2(parts.hour)}:${pad2(parts.minute)}`;
  const hms = `${hm}:${pad2(parts.second)}`;
  switch (pattern) {
    case 'yyyy-mm-dd':
    case 'mm/dd/yyyy':
    case 'm/d/yyyy':
      return date[pattern];
    case 'yyyy-mm-dd hh:mm':
      return `${date['yyyy-mm-dd']} ${hm}`;
    case 'yyyy-mm-dd hh:mm:ss':
      return `${date['yyyy-mm-dd']} ${hms}`;
    case 'hh:mm':
      return hm;
    case 'hh:mm:ss':
      return hms;
  }
}

/** Date layout used when a `time` format is asked to render a full date. */
function datePatternFor(fmt: NumberFormat): DateTimePattern {
  if (fmt.category === 'time') {
    return fmt.pattern === 'hh:mm' || fmt.pattern === 'hh:mm:ss' ? fmt.pattern : 'hh:mm';
  }
  return fmt.pattern;
}

/**
 * The text a numeric value displays under `fmt`, before any width check.
 *
 * A serial outside the four-digit-year calendar falls back to `general`, because
 * a negative date has no representation and silently printing a wrong date would
 * be worse than printing the number.
 */
export function formatNumberText(v: number, fmt: NumberFormat): string {
  if (!Number.isFinite(v)) return '#NUM!';
  switch (fmt.category) {
    case 'general':
    case 'text':
      return generalNumberText(v);
    case 'number': {
      const magnitude = fixedMagnitude(v, fmt.decimals, fmt.grouping);
      return signed(v, magnitude);
    }
    case 'currency': {
      const magnitude = `${fmt.currency}${fixedMagnitude(v, fmt.decimals, fmt.grouping)}`;
      return signed(v, magnitude);
    }
    case 'percentage': {
      const magnitude = `${fixedMagnitude(v * 100, fmt.decimals, fmt.grouping)}%`;
      return signed(v * 100, magnitude);
    }
    case 'date': {
      if (!isDisplayableSerial(v)) return generalNumberText(v);
      return formatDateParts(partsFromSerial(v), datePatternFor(fmt));
    }
    case 'time': {
      if (!isDisplayableSerial(v)) return generalNumberText(v);
      // A time format shows the time of day, so the date part is discarded.
      const timeOnly = v - Math.floor(v);
      return formatDateParts(partsFromSerial(timeOnly), datePatternFor(fmt));
    }
  }
}

/* ------------------------------------------------------------- measurement */

/**
 * Estimated rendered width of `text` in CSS pixels, including cell padding.
 *
 * A proportional-font estimate is enough for the overflow decision: it is
 * monotonic in the character count, deterministic, and identical in the worker
 * and in tests. The Grid_Renderer may refine it with real text metrics for
 * painting, but the `#####` decision stays here so it is testable.
 */
export function measureTextPx(text: string, fontSizePx: number = DEFAULT_FONT_SIZE_PX): number {
  return text.length * fontSizePx * CHAR_WIDTH_RATIO + CELL_PADDING_PX;
}

/** Natural alignment of a value: numbers right, text left, booleans and errors centred. */
function naturalAlign(v: CellValue, fmt: NumberFormat): 'left' | 'center' | 'right' {
  switch (v.k) {
    case 'number':
      return fmt.category === 'text' ? 'left' : 'right';
    case 'text':
      return 'left';
    case 'bool':
    case 'error':
      return 'center';
    case 'empty':
      return 'left';
  }
}

/** The result of formatting one cell for display. */
export interface FormattedText {
  /** What the renderer paints: {@link OVERFLOW_MARKER} when a numeric does not fit. */
  text: string;
  /** The formatted text before any overflow substitution. */
  full: string;
  /** True when the numeric text was replaced by the overflow marker. */
  overflow: boolean;
  /** Measured width of {@link FormattedText.full} in CSS pixels. */
  measuredPx: number;
  /** Alignment implied by the value and the format, before an explicit style. */
  align: 'left' | 'center' | 'right';
}

export interface FormatValueOptions {
  /** Font size used for width measurement. Defaults to {@link DEFAULT_FONT_SIZE_PX}. */
  fontSizePx?: number;
}

/**
 * Formats one cell value for display (Requirements 6.2, 6.7).
 *
 * `widthPx` is the column width. Only a numeric value that does not fit is
 * replaced by `#####`; text, booleans, and error values keep their text and
 * overflow or clip visually, which is Excel's behaviour and what Property 23
 * asserts.
 */
export function formatValue(
  v: CellValue,
  fmt: NumberFormat,
  widthPx: number = Number.POSITIVE_INFINITY,
  opts: FormatValueOptions = {},
): FormattedText {
  const fontSizePx = opts.fontSizePx ?? DEFAULT_FONT_SIZE_PX;
  const align = naturalAlign(v, fmt);

  let full: string;
  switch (v.k) {
    case 'empty':
      full = '';
      break;
    case 'number':
      full = formatNumberText(v.v, fmt);
      break;
    case 'text':
      full = v.v;
      break;
    case 'bool':
      full = v.v ? 'TRUE' : 'FALSE';
      break;
    case 'error':
      full = v.v;
      break;
  }

  const measuredPx = measureTextPx(full, fontSizePx);
  const overflow = v.k === 'number' && full.length > 0 && measuredPx > widthPx;
  return { text: overflow ? OVERFLOW_MARKER : full, full, overflow, measuredPx, align };
}
